import React, { createContext, useContext, useState, useCallback } from 'react';

const GlobalContext = createContext();

export const useGlobal = () => {
  const context = useContext(GlobalContext);
  if (!context) {
    throw new Error('useGlobal must be used within a GlobalProvider');
  }
  return context;
};

export const useProduct = () => {
  const context = useContext(GlobalContext);
  if (!context) {
    throw new Error('useProduct must be used within a GlobalProvider');
  }
  return {
    activeProduct: context.activeProduct,
    setActiveProduct: context.setActiveProduct,
  };
};

export const GlobalProvider = ({ children }) => {
  const [apiKey, setApiKey] = useState(localStorage.getItem('apiKey') || '');
  const [baseServiceUrl, setBaseServiceUrl] = useState(
    localStorage.getItem('baseServiceUrl') || "https://api.mbd.xyz/v3/studio"
  );
  const [accountId, setAccountId] = useState(
    localStorage.getItem('accountId') || ''
  );
  const [defaultFarcasterUserId, setDefaultFarcasterUserId] = useState(
    localStorage.getItem('defaultFarcasterUserId') || ''
  );
  const [defaultPolymarketWalletAddress, setDefaultPolymarketWalletAddress] = useState(
    localStorage.getItem('defaultPolymarketWalletAddress') || ''
  );
  const [defaultKalshiWalletAddress, setDefaultKalshiWalletAddress] = useState(
    localStorage.getItem('defaultKalshiWalletAddress') || ''
  );
  const [currentContext, setCurrentContext] = useState(null);

  // Login mode (ephemeral, not persisted)
  const [loginToken, setLoginToken] = useState(null);
  const [userData, setUserData] = useState(null);

  // Product switcher: build | deploy | eval
  const [activeProduct, setActiveProductState] = useState(
    () => {
      const stored = localStorage.getItem('activeProduct');
      if (stored === 'studio') {
        localStorage.setItem('activeProduct', 'build');
        return 'build';
      }
      return stored || 'build';
    }
  );

  const mergeCurrentContext = useCallback((updates) => {
    setCurrentContext((prev) => {
      const next = { ...(prev || {}) };
      for (const [k, v] of Object.entries(updates)) {
        if (v === undefined) delete next[k];
        else next[k] = v;
      }
      return next;
    });
  }, []);

  const setActiveProduct = useCallback((product) => {
    setActiveProductState(product);
    localStorage.setItem('activeProduct', product);
    setCurrentContext(null); // Reset help agent context when switching products
  }, []);

  const updateBaseServiceUrl = useCallback((url) => {
    setBaseServiceUrl(url);
    localStorage.setItem('baseServiceUrl', url);
  }, []);

  const updateApiKey = useCallback((key) => {
    setApiKey(key);
    localStorage.setItem('apiKey', key);
  }, []);

  const updateDefaultFarcasterUserId = useCallback((userId) => {
    setDefaultFarcasterUserId(userId);
    localStorage.setItem('defaultFarcasterUserId', userId);
  }, []);

  const updateAccountId = useCallback((consoleAccountId) => {
    setAccountId(consoleAccountId);
    localStorage.setItem('accountId', consoleAccountId);
  }, []);

  

  const updateDefaultPolymarketWalletAddress = useCallback((walletAddress) => {
    setDefaultPolymarketWalletAddress(walletAddress);
    localStorage.setItem('defaultPolymarketWalletAddress', walletAddress);
  }, []);

  const updateDefaultKalshiWalletAddress = useCallback((walletAddress) => {
    setDefaultKalshiWalletAddress(walletAddress);
    localStorage.setItem('defaultKalshiWalletAddress', walletAddress);
  }, []);

  const loginSuccess = useCallback((token, data) => {
    setLoginToken(token);
    setUserData(data);
    setAccountId(data.account_id);
    setApiKey(data.api_key);
  }, []);

  const logout = useCallback(() => {
    setLoginToken(null);
    setUserData(null);
  }, []);

  const value = {
    baseServiceUrl,
    apiKey,
    loginToken,
    accountId: accountId,
    userData,
    userId: userData?.user_id,
    defaultFarcasterUserId,
    defaultPolymarketWalletAddress,
    defaultKalshiWalletAddress,
    currentContext,
    setCurrentContext,
    mergeCurrentContext,
    activeProduct,
    setActiveProduct,
    updateApiKey,
    updateBaseServiceUrl,
    updateDefaultFarcasterUserId,
    updateAccountId,
    updateDefaultPolymarketWalletAddress,
    updateDefaultKalshiWalletAddress,
    loginSuccess,
    logout,
  };

  return (
    <GlobalContext.Provider value={value}>
      {children}
    </GlobalContext.Provider>
  );
};
